import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Trash2, Calendar, Users, ChevronDown, ChevronUp, Download,
  Mail, Send, X, CheckSquare, Square, Paperclip, DollarSign
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminShowcaseRegistrations = () => {
  const [showcases, setShowcases] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedShowcase, setExpandedShowcase] = useState(null);
  const [message, setMessage] = useState(null);

  // Email modal state
  const [emailModal, setEmailModal] = useState(null); // { mode: 'individual'|'bulk', registration?, showcase? }
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', cc: '', bcc: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(null);

  // Payment toggle inflight
  const [togglingPaymentId, setTogglingPaymentId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [showcasesRes, registrationsRes] = await Promise.all([
        axios.get(`${API}/showcases`),
        axios.get(`${API}/showcase-registrations`)
      ]);
      setShowcases(showcasesRes.data);
      setRegistrations(registrationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const flashMessage = (type, text, ms = 4000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), ms);
  };

  const handleDelete = async (registrationId) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;
    try {
      await axios.delete(`${API}/showcase-registrations/${registrationId}`);
      setRegistrations(prev => prev.filter(r => r.id !== registrationId));
      flashMessage('success', 'Registration deleted');
    } catch (error) {
      console.error('Error deleting registration:', error);
      flashMessage('error', 'Error deleting registration. Please try again.');
    }
  };

  const handleTogglePayment = async (registration) => {
    setTogglingPaymentId(registration.id);
    try {
      const next = !registration.paid;
      const res = await axios.post(`${API}/showcase-registrations/${registration.id}/payment`, { paid: next });
      setRegistrations(prev => prev.map(r =>
        r.id === registration.id ? { ...r, paid: next, paid_at: res.data.paid_at } : r
      ));
      flashMessage('success', `Marked ${registration.player_name} as ${next ? 'PAID' : 'UNPAID'}`);
    } catch (error) {
      flashMessage('error', error.response?.data?.detail || 'Error updating payment status');
    } finally {
      setTogglingPaymentId(null);
    }
  };

  const openIndividualEmail = (registration) => {
    setEmailModal({ mode: 'individual', registration });
    setEmailForm({ subject: '', body: '', cc: '', bcc: '' });
  };

  const openBulkEmail = (showcase) => {
    setEmailModal({ mode: 'bulk', showcase });
    setEmailForm({ subject: '', body: '', cc: '', bcc: '' });
  };

  const closeEmailModal = () => {
    setEmailModal(null);
    setEmailForm({ subject: '', body: '', cc: '', bcc: '' });
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) {
      flashMessage('error', 'Subject and message are required');
      return;
    }
    setSendingEmail(true);
    try {
      let response;
      let recipients = [];
      if (emailModal.mode === 'individual') {
        response = await axios.post(`${API}/showcase-registrations/send-email`, {
          registration_id: emailModal.registration.id,
          subject: emailForm.subject,
          body: emailForm.body,
          cc: emailForm.cc.trim() || null,
          bcc: emailForm.bcc.trim() || null,
        });
        recipients = [{ name: emailModal.registration.player_name, email: emailModal.registration.email }];
      } else {
        response = await axios.post(`${API}/showcase-registrations/send-bulk-email`, {
          showcase_id: emailModal.showcase.id,
          subject: emailForm.subject,
          body: emailForm.body,
          cc: emailForm.cc.trim() || null,
          bcc: emailForm.bcc.trim() || null,
        });
        recipients = getRegistrationsForShowcase(emailModal.showcase.id).map(r => ({ name: r.player_name, email: r.email }));
      }
      if (emailForm.cc.trim()) recipients.push({ name: 'CC', email: emailForm.cc.trim() });
      if (emailForm.bcc.trim()) recipients.push({ name: 'BCC', email: emailForm.bcc.trim() });
      setEmailSuccess({
        subject: emailForm.subject,
        recipients,
        sent: response.data.sent || 1,
        failed: response.data.failed || 0,
        isBulk: emailModal.mode === 'bulk',
        showcaseName: emailModal.showcase?.name,
      });
      closeEmailModal();
    } catch (error) {
      flashMessage('error', error.response?.data?.detail || 'Error sending email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleExportAll = () => {
    window.open(`${API}/showcase-registrations/export/csv`, '_blank');
  };

  const handleExportShowcase = (showcaseId) => {
    window.open(`${API}/showcase-registrations/export/csv?showcase_id=${showcaseId}`, '_blank');
  };

  const getRegistrationsForShowcase = (showcaseId) =>
    registrations.filter(r => r.showcase_id === showcaseId);

  const toggleShowcase = (showcaseId) => {
    setExpandedShowcase(prev => prev === showcaseId ? null : showcaseId);
  };

  const th = { padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-light)' };
  const td = { padding: '12px 16px' };
  const iconBtn = (color) => ({
    padding: '6px 10px',
    background: `${color}22`,
    color,
    border: `1px solid ${color}66`,
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.8rem',
    fontWeight: 600,
  });

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <Link to="/admin/dashboard" className="btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeft size={20} /> Back to Dashboard
              </Link>
              <h1 className="display-large">Showcase Registrations</h1>
              <p className="body-large">View, email and track payment for showcase signups</p>
            </div>
            {registrations.length > 0 && (
              <button
                onClick={handleExportAll}
                className="btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Download size={20} /> Export All to CSV
              </button>
            )}
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {message && (
            <div style={{
              padding: '12px 16px',
              background: message.type === 'success' ? 'rgba(34,197,94,0.18)' : 'rgba(220,38,38,0.18)',
              border: `1px solid ${message.type === 'success' ? '#22c55e' : '#dc2626'}`,
              borderRadius: '8px', marginBottom: '20px',
              color: message.type === 'success' ? '#22c55e' : '#ef4444',
            }} data-testid="showcase-flash">
              {message.text}
            </div>
          )}

          {loading ? (
            <p>Loading registrations...</p>
          ) : showcases.length === 0 ? (
            <div className="network-card" style={{ textAlign: 'center', padding: '48px' }}>
              <p>No showcases found. Create showcases first to receive registrations.</p>
              <Link to="/admin/showcases" className="btn-primary" style={{ marginTop: '16px', display: 'inline-block' }}>
                Manage Showcases
              </Link>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {showcases.map((showcase) => {
                const showcaseRegistrations = getRegistrationsForShowcase(showcase.id);
                const isExpanded = expandedShowcase === showcase.id;
                const paidCount = showcaseRegistrations.filter(r => r.paid).length;
                const unpaidCount = showcaseRegistrations.length - paidCount;

                return (
                  <div key={showcase.id} className="network-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <button
                      onClick={() => toggleShowcase(showcase.id)}
                      style={{
                        width: '100%', padding: '20px 24px', background: 'transparent', border: 'none',
                        cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', textAlign: 'left'
                      }}
                      data-testid={`showcase-toggle-${showcase.id}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Calendar size={24} style={{ color: 'var(--brand-primary)' }} />
                        <div>
                          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>{showcase.name}</h3>
                          <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-light)' }}>
                            {new Date(showcase.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} • {showcase.location}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{
                          padding: '6px 12px',
                          background: showcaseRegistrations.length > 0 ? 'rgba(212, 175, 55, 0.2)' : 'rgba(100, 100, 100, 0.2)',
                          borderRadius: '20px', fontSize: '0.875rem', fontWeight: 600,
                          color: showcaseRegistrations.length > 0 ? 'var(--brand-primary)' : 'var(--text-light)',
                          display: 'flex', alignItems: 'center', gap: '6px'
                        }}>
                          <Users size={16} />
                          {showcaseRegistrations.length} {showcaseRegistrations.length === 1 ? 'Registration' : 'Registrations'}
                        </span>
                        {showcaseRegistrations.length > 0 && (
                          <span style={{
                            padding: '6px 12px',
                            background: 'rgba(34,197,94,0.15)', color: '#22c55e',
                            borderRadius: '20px', fontSize: '0.875rem', fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: '6px'
                          }} data-testid={`paid-summary-${showcase.id}`}>
                            <DollarSign size={14} />
                            {paidCount} paid / {unpaidCount} unpaid
                          </span>
                        )}
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div style={{ borderTop: '1px solid var(--border-light)', background: 'rgba(0,0,0,0.2)' }}>
                        {showcaseRegistrations.length === 0 ? (
                          <p style={{ padding: '24px', textAlign: 'center', color: 'var(--text-light)' }}>
                            No registrations yet for this showcase.
                          </p>
                        ) : (
                          <>
                            <div style={{ padding: '16px 16px 0', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); openBulkEmail(showcase); }}
                                style={{ padding: '8px 16px', background: 'var(--brand-primary)', color: 'black', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                                data-testid={`bulk-email-${showcase.id}`}
                              >
                                <Send size={16} /> Email All Registrants
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleExportShowcase(showcase.id); }}
                                className="btn-secondary"
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.875rem', padding: '8px 16px' }}
                              >
                                <Download size={16} /> Export This Showcase
                              </button>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                                    <th style={th}>Paid</th>
                                    <th style={th}>Player Name</th>
                                    <th style={th}>Email</th>
                                    <th style={th}>Phone</th>
                                    <th style={th}>Grad Year</th>
                                    <th style={th}>High School</th>
                                    <th style={th}>Position(s)</th>
                                    <th style={th}>Parent</th>
                                    <th style={th}>Registered</th>
                                    <th style={th}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {showcaseRegistrations.map((reg) => (
                                    <tr key={reg.id} style={{
                                      borderBottom: '1px solid var(--border-light)',
                                      background: reg.paid ? 'rgba(34,197,94,0.05)' : 'transparent'
                                    }} data-testid={`registration-row-${reg.id}`}>
                                      <td style={td}>
                                        <button
                                          onClick={() => handleTogglePayment(reg)}
                                          disabled={togglingPaymentId === reg.id}
                                          title={reg.paid ? `Paid on ${reg.paid_at ? new Date(reg.paid_at).toLocaleDateString() : 'unknown date'}` : 'Click to mark as paid'}
                                          style={{
                                            background: 'transparent', border: 'none', cursor: togglingPaymentId === reg.id ? 'wait' : 'pointer',
                                            padding: '4px', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                            color: reg.paid ? '#22c55e' : 'var(--text-light)',
                                          }}
                                          data-testid={`paid-toggle-${reg.id}`}
                                        >
                                          {reg.paid ? <CheckSquare size={20} /> : <Square size={20} />}
                                          <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                            {reg.paid ? 'PAID' : 'UNPAID'}
                                          </span>
                                        </button>
                                      </td>
                                      <td style={{ ...td, fontWeight: 500 }}>{reg.player_name}</td>
                                      <td style={td}>
                                        <a href={`mailto:${reg.email}`} style={{ color: 'var(--brand-primary)' }}>{reg.email}</a>
                                      </td>
                                      <td style={td}>
                                        <a href={`tel:${reg.phone}`} style={{ color: 'var(--brand-primary)' }}>{reg.phone}</a>
                                      </td>
                                      <td style={td}>
                                        <span style={{
                                          padding: '4px 8px', background: 'var(--brand-primary)', color: 'black',
                                          borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600
                                        }}>{reg.graduation_year}</span>
                                      </td>
                                      <td style={td}>{reg.high_school}</td>
                                      <td style={td}>{reg.positions}</td>
                                      <td style={td}>{reg.parent_name || '-'}</td>
                                      <td style={{ ...td, fontSize: '0.875rem', color: 'var(--text-light)' }}>
                                        {new Date(reg.registered_at).toLocaleDateString()}
                                      </td>
                                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        <div style={{ display: 'inline-flex', gap: '6px' }}>
                                          <button
                                            onClick={() => openIndividualEmail(reg)}
                                            style={iconBtn('#3b82f6')}
                                            data-testid={`email-reg-${reg.id}`}
                                          >
                                            <Mail size={14} /> Email
                                          </button>
                                          <button
                                            onClick={() => handleDelete(reg.id)}
                                            style={iconBtn('#dc2626')}
                                            data-testid={`delete-reg-${reg.id}`}
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Email Modal */}
      {emailModal && (
        <div style={modalBackdrop} data-testid="showcase-email-modal">
          <div style={modalCard}>
            <button onClick={closeEmailModal} style={modalCloseBtn}><X size={22} /></button>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <Mail size={24} style={{ color: emailModal.mode === 'bulk' ? 'var(--brand-primary)' : '#3b82f6' }} />
                <h2 style={{ margin: 0, color: '#fff' }}>
                  {emailModal.mode === 'bulk'
                    ? `Email All Registrants — ${emailModal.showcase.name}`
                    : `Email ${emailModal.registration.player_name}`}
                </h2>
              </div>
              <p style={{ margin: 0, color: '#999', fontSize: '0.9rem' }}>
                {emailModal.mode === 'bulk'
                  ? `To: all ${getRegistrationsForShowcase(emailModal.showcase.id).length} registrant(s)`
                  : `To: ${emailModal.registration.email}`}
              </p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={modalLabel}>Subject *</label>
              <input
                type="text"
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                style={modalInput}
                data-testid="showcase-email-subject"
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={modalLabel}>Message *</label>
              <textarea
                rows={8}
                value={emailForm.body}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                style={{ ...modalInput, resize: 'vertical' }}
                data-testid="showcase-email-body"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={modalLabel}>CC</label>
                <input type="email" value={emailForm.cc} onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })} style={modalInput} />
              </div>
              <div>
                <label style={modalLabel}>BCC</label>
                <input type="email" value={emailForm.bcc} onChange={(e) => setEmailForm({ ...emailForm, bcc: e.target.value })} style={modalInput} />
              </div>
            </div>
            <div style={{ padding: '10px 14px', background: 'rgba(212,175,55,0.1)', borderRadius: '8px', marginBottom: '16px', display: 'flex', gap: '10px' }}>
              <Paperclip size={16} style={{ color: 'var(--brand-primary)', marginTop: '2px' }} />
              <div style={{ fontSize: '0.8rem', color: '#ccc' }}>Paste URLs directly in the message for links/attachments.</div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailForm.subject.trim() || !emailForm.body.trim()}
                style={{
                  flex: 1, padding: '12px',
                  background: sendingEmail ? '#666' : 'var(--brand-primary)',
                  color: 'black', border: 'none', borderRadius: '8px',
                  fontWeight: 700, cursor: sendingEmail ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                }}
                data-testid="showcase-email-send"
              >
                <Send size={16} /> {sendingEmail ? 'Sending...' : 'Send Email'}
              </button>
              <button onClick={closeEmailModal} style={{ padding: '12px 20px', background: 'transparent', color: '#999', border: '1px solid #444', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Email Success Popup */}
      {emailSuccess && (
        <div style={{ ...modalBackdrop, zIndex: 1002 }} data-testid="showcase-email-success">
          <div style={{ ...modalCard, border: '2px solid #22c55e', maxWidth: '500px', textAlign: 'center' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Send size={28} style={{ color: '#22c55e' }} />
            </div>
            <h2 style={{ color: '#22c55e', marginBottom: '6px' }}>Email Sent!</h2>
            <p style={{ color: '#ccc', marginBottom: '16px' }}>
              {emailSuccess.isBulk ? `Sent to registrants for ${emailSuccess.showcaseName}` : 'Email delivered successfully'}
            </p>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '14px', marginBottom: '16px', textAlign: 'left' }}>
              <div style={{ marginBottom: '10px' }}>
                <span style={{ color: '#999', fontSize: '0.8rem' }}>Subject:</span>
                <p style={{ color: '#fff', margin: '2px 0 0', fontWeight: 500 }}>{emailSuccess.subject}</p>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <span style={{ color: '#999', fontSize: '0.8rem' }}>Sent to:</span>
                <p style={{ color: '#22c55e', margin: '2px 0 0', fontWeight: 600 }}>{emailSuccess.sent} recipient{emailSuccess.sent !== 1 ? 's' : ''}</p>
              </div>
              {emailSuccess.recipients?.length > 0 && (
                <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
                  {emailSuccess.recipients.slice(0, 10).map((r, i) => (
                    <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #333', fontSize: '0.85rem' }}>
                      <span style={{ color: '#fff' }}>{r.name}</span>
                      <span style={{ color: '#666', marginLeft: '6px' }}>({r.email})</span>
                    </div>
                  ))}
                  {emailSuccess.recipients.length > 10 && (
                    <p style={{ color: '#999', fontSize: '0.8rem', marginTop: '6px' }}>+{emailSuccess.recipients.length - 10} more</p>
                  )}
                </div>
              )}
              {emailSuccess.failed > 0 && (
                <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(239,68,68,0.2)', borderRadius: '4px' }}>
                  <span style={{ color: '#ef4444', fontSize: '0.8rem' }}>{emailSuccess.failed} failed</span>
                </div>
              )}
            </div>
            <button onClick={() => setEmailSuccess(null)} style={{ padding: '10px 28px', background: '#22c55e', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }} data-testid="showcase-email-success-done">Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

const modalBackdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '20px' };
const modalCard = { background: '#1a1a1a', borderRadius: '14px', padding: '28px', maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative', border: '1px solid var(--border-medium)' };
const modalCloseBtn = { position: 'absolute', top: '14px', right: '14px', background: 'none', border: 'none', cursor: 'pointer', color: '#999' };
const modalLabel = { display: 'block', marginBottom: '6px', color: '#ccc', fontWeight: 500, fontSize: '0.85rem' };
const modalInput = { width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: '#fff', fontSize: '0.95rem' };

export default AdminShowcaseRegistrations;
